/**
 * Calendar helpers for the 8,760-hour engine (non-leap 2026 calendar, Jan 1 is
 * a Thursday). Ported from ES `src/types/load.ts`.
 */

export const HOURS_IN_YEAR = 8760;

export const MONTH_HOURS = [744, 672, 744, 720, 744, 720, 744, 744, 720, 744, 720, 744] as const;

export const MONTH_START_HOUR = [0, 744, 1416, 2160, 2880, 3624, 4344, 5088, 5832, 6552, 7296, 8016] as const;

/** Days per month (non-leap). */
export const MONTH_DAYS = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31] as const;

/** Short month labels for charts. */
export const MONTH_LABELS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"] as const;

/** 2026 starts on Thursday (0 = Sunday … 6 = Saturday). */
export const START_DAY_OF_WEEK_2026 = 4;

/** Returns month index (0–11) for an hour of the year (0–8759). */
export function getMonthFromHour(hour: number): number {
  if (hour < 0 || hour >= HOURS_IN_YEAR) {
    throw new RangeError(`Hour index ${hour} is out of bounds [0, ${HOURS_IN_YEAR - 1}]`);
  }
  for (let m = 11; m >= 0; m--) {
    if (hour >= MONTH_START_HOUR[m]) return m;
  }
  return 0;
}

/** Returns start hour and length in hours for a month (0–11). */
export function getMonthRange(month: number): { startHour: number; hours: number } {
  if (month < 0 || month > 11 || !Number.isInteger(month)) {
    throw new RangeError(`Month index ${month} is out of bounds [0, 11]`);
  }
  return { startHour: MONTH_START_HOUR[month], hours: MONTH_HOURS[month] };
}

/** Returns hour of day (0–23) for an hour of the year. */
export function getHourOfDay(hour: number): number {
  return ((hour % 24) + 24) % 24;
}

/** Returns day of year (0–364) for an hour of the year. */
export function getDayOfYear(hour: number): number {
  return Math.floor(hour / 24);
}

/** True if the hour falls on a Saturday or Sunday (2026 calendar). */
export function isWeekend(hour: number): boolean {
  const dow = (START_DAY_OF_WEEK_2026 + getDayOfYear(hour)) % 7;
  return dow === 0 || dow === 6;
}

/** Sums an 8,760 vector into 12 monthly totals. */
export function sumByMonth(vec: ArrayLike<number>): number[] {
  if (vec.length !== HOURS_IN_YEAR) {
    throw new Error(`Expected ${HOURS_IN_YEAR} hours, got ${vec.length}`);
  }
  const out = new Array<number>(12).fill(0);
  for (let m = 0; m < 12; m++) {
    const start = MONTH_START_HOUR[m];
    const end = start + MONTH_HOURS[m];
    let s = 0;
    for (let h = start; h < end; h++) s += vec[h];
    out[m] = s;
  }
  return out;
}

/** Sums an 8,760 vector. */
export function sumVector(vec: ArrayLike<number>): number {
  let s = 0;
  for (let i = 0; i < vec.length; i++) s += vec[i];
  return s;
}
